import {
  CircleStackIcon,
  PlusIcon,
  ShieldCheckIcon,
  StarIcon,
  TrashIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { AppRoutesList, User, UserRoleEnum } from '@qse/constants'
import { useBillingStatusStore, useUserStore } from '@qse/stores'
import { type MenuItem, Navbar, QuotaAdjustmentBanner, Sidebar, useDriveSetup } from '@qse/ui'
import { getKeycloakHandoff, getQpaUrl, isKeycloakAuth, truncateToDecimals, useIsMobile } from '@qse/utils'
import { type FC, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { KeyProtectionProvider } from '@/contexts/key-protection-context'
import { useBillingStatus } from '@/hooks'
import { useScrollToTop } from '@/hooks/use-scroll-to-top'

interface DashboardLayoutProps {
  children?: React.ReactNode
}

const DashboardLayoutContent: FC<DashboardLayoutProps> = () => {
  const [isOpen, setIsOpen] = useState(true)
  const { user } = useUserStore()
  const { showQuotaAdjustmentBanner, quotaAdjustmentGracePeriodStatus } = useBillingStatusStore()
  const isMobile = useIsMobile()
  const isTenantAdmin = user?.userRole === UserRoleEnum.TENANT_ADMIN

  useBillingStatus()

  // Handle drive setup modal
  const { needsSetup } = useDriveSetup({ driveStatus: user?.drive?.status })

  // Automatically scroll to top when routes change
  // This ensures users start at the top of each new page they navigate to
  useScrollToTop()

  // Dashboard security submenu items
  const securitySubmenuItems: Array<{ label: string; redirectUrl: string }> = [
    {
      label: 'Generate Keys',
      redirectUrl: AppRoutesList.generateKeys as string,
    },
    {
      label: 'Generate Entropy',
      redirectUrl: AppRoutesList.generateEntropy as string,
    },
  ]

  const quotaSummary = useMemo(() => user?.drive?.quotaSummary, [user])
  const qpaSummary = useMemo(
    () => user?.drive?.quotaSummary?.qpa?.quota ?? user?.qpaQuota?.qpaSeeds ?? 0,
    [user],
  )

  // Use real quota data or fallback to defaults
  const used = quotaSummary?.storage?.used || 0
  const quota = quotaSummary?.storage?.quota || 0
  const qpa = qpaSummary || 0
  const pendingBytes = quotaSummary?.storage?.pendingBytes || 0

  const openQpa = async () => {
    const plain = getQpaUrl('/')

    if (!isKeycloakAuth()) {
      window.open(plain, '_blank', 'noopener')

      return
    }

    const handoff = await getKeycloakHandoff()

    if (!handoff) {
      window.open(plain, '_blank', 'noopener')

      return
    }

    const fragment = new URLSearchParams()

    if (handoff.refreshToken) fragment.set('rt', handoff.refreshToken)
    fragment.set('at', handoff.accessToken)
    // Fragment is not sent to the server. QPrime reads it once and strips it.
    window.open(`${getQpaUrl('/callback')}#${fragment.toString()}`, '_blank', 'noopener')
  }

  const dashboardMenuItems = useMemo(() => {
    const items = [
      { id: 'new', label: 'New', icon: PlusIcon, isPrimary: true },
      {
        id: 'my-drive',
        label: isMobile ? 'Drive' : 'My Drive',
        icon: CircleStackIcon,
        redirectUrl: AppRoutesList.dashboard,
      },
      {
        id: 'favorites',
        label: 'Favorites',
        icon: StarIcon,
        hasAvatar: false,
        redirectUrl: AppRoutesList.favorites,
      },
      { id: 'trash', label: 'Trash', icon: TrashIcon, redirectUrl: AppRoutesList.trash },
      ...(qpa > 0
        ? [
            {
              id: 'qpa',
              label: 'QPA',
              icon: ShieldCheckIcon,
              externalIcon: ShieldCheckIcon,
              isExternal: true,
              onExternalOpen: openQpa,
            },
          ]
        : []),
      { id: 'profile', label: 'Profile', icon: UserIcon, redirectUrl: '/profile' },
    ]

    return items as MenuItem[]
  }, [isMobile, qpa])

  // Calculate percentages: if quota is 0 and used > 0, show 100% (over-quota)
  const usedPercent =
    quota === 0
      ? used > 0
        ? 100 // Show 100% when quota is 0 and used > 0
        : 0
      : quota && used
        ? truncateToDecimals((used / quota) * 100)
        : 0

  const percentage =
    quota === 0
      ? used + pendingBytes > 0
        ? 100 // Show 100% when quota is 0 and allocated > 0
        : 0
      : quota && used + pendingBytes
        ? truncateToDecimals(((used + pendingBytes) / quota) * 100)
        : 0

  const storageInfo = {
    used,
    remaining: quotaSummary?.storage?.remaining || 0,
    total: quota,
    usedPercent,
    percentage,
    isUploading: false,
    isDeleting: false,
    pendingBytes,
  }

  const apiQuotaInfo = {
    used: quotaSummary?.service?.used || 0,
    total: quotaSummary?.service?.quota || 0,
    percentage:
      quotaSummary?.service?.quota && quotaSummary?.service?.used
        ? truncateToDecimals((quotaSummary.service.used / quotaSummary.service.quota) * 100)
        : 0,
    isActive: false,
  }

  const shouldShowQuotaAdjustmentBanner =
    isTenantAdmin &&
    showQuotaAdjustmentBanner &&
    !!quotaAdjustmentGracePeriodStatus?.hasActiveGracePeriod &&
    !!quotaAdjustmentGracePeriodStatus?.targetPlan

  // For SUPER_ADMIN users, provide unlimited quota display
  if (user?.userRole === UserRoleEnum.SUPER_ADMIN) {
    storageInfo.total = Number.MAX_SAFE_INTEGER
    storageInfo.used = 0
    storageInfo.remaining = 0
    storageInfo.percentage = 0

    apiQuotaInfo.total = Number.MAX_SAFE_INTEGER
    apiQuotaInfo.used = 0
    apiQuotaInfo.percentage = 0
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-black">
      <aside
        className={`
          bg-white dark:bg-[#282828] text-gray-900 dark:text-white
          transition-all duration-300 ease-in-out
        `}
      >
        <Sidebar
          apiQuotaInfo={apiQuotaInfo}
          isOpen={isOpen}
          menuItems={dashboardMenuItems}
          securitySubmenuItems={securitySubmenuItems}
          setIsOpen={setIsOpen}
          storageInfo={storageInfo}
          user={user as User}
        />
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 ${location.pathname === '/dashboard/' || location.pathname === '/dashboard' || location.pathname.includes('/profile') || location.pathname.includes('/dashboard/generate-keys') || location.pathname.includes('/dashboard/generate-entropy') ? 'overflow-y-scroll' : ''} scrollbar-hide overflow-hidden  bg-gray-50 dark:bg-black text-gray-900 dark:text-white ${needsSetup ? 'blur-sm pointer-events-none select-none' : ''}`}
      >
        <Navbar isTenantAdmin={isTenantAdmin} />

        <div className="">
          <Outlet />
        </div>
        {shouldShowQuotaAdjustmentBanner && <QuotaAdjustmentBanner />}
      </main>
      {/* Global Progress Trackers */}
    </div>
  )
}

const DashboardLayout: FC<DashboardLayoutProps> = () => {
  return (
    <KeyProtectionProvider>
      <DashboardLayoutContent />
    </KeyProtectionProvider>
  )
}

export default DashboardLayout
